import { ApiDefinition, ApiRequest, ApiResponse, callApi } from "@/api/call-api";
import { getQuerySession } from "@/api/query-client";
import { createSessionMutation } from "@/api/session-mutation";
import { useMutation, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

type Options<T extends ApiDefinition, TContext> = Omit<UseMutationOptions<ApiResponse<T>, Error, ApiRequest<T>, TContext>, "mutationFn"> & {
  api: T;
  disableLoader?: boolean;
};

/** TanStack Query write hook for new code. Pass the request to mutate/mutateAsync; update or invalidate read caches on success. */
export default function useApiMutation<T extends ApiDefinition, TContext = unknown>({
  api,
  disableLoader = false,
  ...options
}: Options<T, TContext>): UseMutationResult<ApiResponse<T>, Error, ApiRequest<T>, TContext> {
  const session = getQuerySession();
  const guarded = createSessionMutation<ApiResponse<T>, ApiRequest<T>, TContext>(getQuerySession, request => callApi(api, request), {
    ...options,
    mutationKey: options.mutationKey ?? [api.Name],
    meta: { ...options.meta, apiName: api.Name, disableLoader },
  });
  const mutation = useMutation(guarded.mutationOptions);
  const guardedRef = useRef(guarded);
  guardedRef.current = guarded;
  const { mutateAsync: runMutation } = mutation;
  const mutateAsync: UseMutationResult<ApiResponse<T>, Error, ApiRequest<T>, TContext>["mutateAsync"] = useCallback(
    (...args) => {
      const [request, callbacks] = args;
      const currentGuard = guardedRef.current;
      const invocation = currentGuard.invocation(request as ApiRequest<T>);
      // A retained handler from an earlier render must not start a new-session request.
      invocation.session = session;
      return currentGuard.settle(runMutation(invocation, currentGuard.callbacks(callbacks)), invocation);
    },
    [runMutation, session]
  );
  const mutate: UseMutationResult<ApiResponse<T>, Error, ApiRequest<T>, TContext>["mutate"] = useCallback(
    (...args) => {
      void mutateAsync(...args).catch(() => undefined);
    },
    [mutateAsync]
  );
  // Unwrapping variables preserves TanStack's status-dependent result shape.
  return {
    ...mutation,
    variables: mutation.variables?.variables,
    mutateAsync,
    mutate,
  } as UseMutationResult<ApiResponse<T>, Error, ApiRequest<T>, TContext>;
}
